import { ArrowUpRight, Bot, CalendarDays, HeartPulse } from "lucide-react";
import { Link } from "react-router-dom";

type TodayPrioritiesProps = {
  appointments?: number;
  highRiskPatients?: number;
  aiEngagements?: number;
  loading?: boolean;
};

export function TodayPriorities({
  loading,
  appointments,
  highRiskPatients,
  aiEngagements,
}: TodayPrioritiesProps) {
  if (loading || appointments == null || highRiskPatients == null || aiEngagements == null) {
    return <p role="status" className="text-sm text-app-fg-muted">{loading ? "Carregando prioridades…" : "Prioridades indisponíveis até a confirmação dos indicadores."}</p>;
  }

  const hasActivity =
    appointments > 0 || highRiskPatients > 0 || aiEngagements > 0;

  if (!hasActivity) {
    return (
      <section className="overflow-hidden rounded-2xl border border-app-border bg-app-surface shadow-premium-sm">
        <div className="flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-brand">
              Prioridades de hoje
            </p>
            <h2 className="mt-1.5 text-lg font-semibold tracking-tight text-app-fg-strong">
              Nenhuma atividade registrada no período
            </h2>
            <p className="mt-1 text-sm text-app-fg-muted">
              Confira o estado do atendimento automático na Equipe de IA.
            </p>
          </div>
          <Link
            to="/growth-team"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-brand-fg transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          >
            Ver Equipe de IA
            <ArrowUpRight className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-5 shadow-premium-sm">
      <header className="mb-4 flex items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-brand">
            Prioridades de hoje
          </p>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-app-fg-strong">
            Sua clínica em movimento
          </h2>
        </div>
        <span className="hidden text-xs text-app-fg-subtle sm:block">
          Dados operacionais reais
        </span>
      </header>

      <div className="grid gap-2 md:grid-cols-3">
        {appointments > 0 && (
          <PriorityLink
            icon={CalendarDays}
            title={`${appointments} agendamentos para acompanhar`}
            action="Ver agenda"
            to="/schedulings"
          />
        )}
        {highRiskPatients > 0 && (
          <PriorityLink
            icon={HeartPulse}
            title={`${highRiskPatients} pacientes pedem atenção`}
            action="Ver pacientes"
            to="/patients"
            warm
          />
        )}
        {aiEngagements > 0 && (
          <PriorityLink
            icon={Bot}
            title={`${aiEngagements} atendimentos feitos pela equipe IA`}
            action="Ver equipe"
            to="/growth-team"
          />
        )}
      </div>
    </section>
  );
}

function PriorityLink({
  icon: Icon,
  title,
  action,
  to,
  warm = false,
}: {
  icon: typeof Bot;
  title: string;
  action: string;
  to: string;
  warm?: boolean;
}) {
  return (
    <Link
      to={to}
      aria-label={action}
      className="group flex min-w-0 items-start gap-3 rounded-xl border border-transparent bg-app-surface-muted p-3.5 transition-[border-color,background-color] hover:border-brand/20 hover:bg-brand-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${
          warm
            ? "bg-accent-warm-soft text-accent-warm-strong"
            : "bg-app-surface text-brand"
        }`}
      >
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold leading-snug text-app-fg-strong">
          {title}
        </span>
        <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand">
          {action}
          <ArrowUpRight
            className="size-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </span>
      </span>
    </Link>
  );
}
